/**
 * Rotation score: a small client-side cut of the harness RHYTHM metrics (packages/harness/src/
 * rhythm.ts, rotation-rework §7). The harness module runs headless beside the sim and isn't a web
 * dependency, so this re-implements the three numbers a player can act on, with the same
 * definitions:
 *  - Always casting: share of in-combat samples (every 250 ms) where you were NOT idle. Idle =
 *    alive, able to act, not casting, GCD ready, and some rotation button (1–6) was castable
 *    (rhythm's idle-but-not-starved);
 *  - Procs used: proc buffs (AuraDef.ui.proc) gained vs. lost unused (expired or overcapped);
 *  - Transforms used: transform windows (AuraDef.overrides) whose form you pressed.
 * Score = 60% always casting + 30% procs + 10% transforms.
 */
import type { AbilityDef, ContentRegistry, DeepReadonly, SimEvent, SimQueries, UnitId, World } from '@keystone/sim';
import { TICK_MS } from '@keystone/sim';
import { abilityForBind } from '../input/bindings';

const SAMPLE_EVERY = Math.max(1, Math.round(250 / TICK_MS));
const ROTATION = ['1', '2', '3', '4', '5', '6'] as const;

export interface RotationCounters {
  samples: number;
  idle: number;
  procsGained: number;
  procsWasted: number;
  wastedBy: Record<string, number>;
  windows: number;
  windowsUsed: number;
}

export interface RotationScore {
  score: number;
  grade: 'S' | 'A' | 'B' | 'C' | 'D';
  /** 0..1 */
  alwaysCasting: number;
  procsGained: number;
  procsWasted: number;
  windows: number;
  windowsUsed: number;
  /** Enough combat sampled to mean anything (≥ 10 s). */
  meaningful: boolean;
}

export function emptyCounters(): RotationCounters {
  return { samples: 0, idle: 0, procsGained: 0, procsWasted: 0, wastedBy: {}, windows: 0, windowsUsed: 0 };
}

export function gradeOf(score: number): RotationScore['grade'] {
  return score >= 90 ? 'S' : score >= 80 ? 'A' : score >= 65 ? 'B' : score >= 50 ? 'C' : 'D';
}

/** Pure: counters → score. */
export function scoreOf(c: RotationCounters): RotationScore {
  const abc = c.samples > 0 ? 1 - c.idle / c.samples : 1;
  const procs = c.procsGained > 0 ? Math.max(0, 1 - c.procsWasted / c.procsGained) : 1;
  const tf = c.windows > 0 ? c.windowsUsed / c.windows : 1;
  const score = Math.round(100 * (0.6 * abc + 0.3 * procs + 0.1 * tf));
  return { score, grade: gradeOf(score), alwaysCasting: abc, procsGained: c.procsGained, procsWasted: c.procsWasted, windows: c.windows, windowsUsed: c.windowsUsed, meaningful: c.samples * 250 >= 10_000 };
}

/** Feeds counters from the live sim (one player). `active()` gates sampling (e.g. graduation only). */
export class RotationTracker {
  readonly c: RotationCounters = emptyCounters();
  private tick = 0;
  private readonly openWindows = new Map<string, string>();

  constructor(
    private readonly content: ContentRegistry,
    private readonly queries: SimQueries,
    private readonly playerId: UnitId,
    private readonly active: () => boolean = () => true,
    /** "In the fight" (default: the player is in combat). The tutorial counts the whole graduation pull. */
    private readonly fighting: (world: DeepReadonly<World>, me: DeepReadonly<World>['units'][string]) => boolean = (_w, me) => me.inCombat,
  ) {}

  step(events: readonly SimEvent[], world: DeepReadonly<World>): void {
    const on = this.active();
    for (const e of events) this.event(e, on);
    if (!on) return;
    if (++this.tick % SAMPLE_EVERY !== 0) return;
    this.sample(world);
  }

  score(): RotationScore {
    return scoreOf(this.c);
  }

  private event(e: SimEvent, on: boolean): void {
    const me = this.playerId;
    if ((e.type === 'applybuff' || e.type === 'applybuffstack') && e.targetID === me) {
      const def = this.content.aura(e.abilityGameID);
      if (!def || !on) return;
      if (def.ui?.proc) this.c.procsGained++;
      if (def.overrides?.length && e.type === 'applybuff') {
        this.openWindows.set(def.id, def.overrides[0]!.with);
        this.c.windows++;
      }
    } else if (e.type === 'removebuff' && e.targetID === me) {
      const def = this.content.aura(e.abilityGameID);
      if (on && def?.ui?.proc && e.reason === 'expired') {
        this.c.procsWasted++;
        this.c.wastedBy[def.id] = (this.c.wastedBy[def.id] ?? 0) + 1;
      }
      this.openWindows.delete(e.abilityGameID);
    } else if (e.type === 'overcap' && (e.targetID ?? e.sourceID) === me && e.kind === 'auraStacks' && on) {
      const def = this.content.aura(e.abilityGameID);
      if (def?.ui?.proc) {
        this.c.procsWasted += e.amount;
        this.c.wastedBy[def.id] = (this.c.wastedBy[def.id] ?? 0) + e.amount;
      }
    } else if (e.type === 'cast' && e.sourceID === me) {
      for (const [aura, withId] of this.openWindows) {
        if (withId === e.abilityGameID) {
          if (on) this.c.windowsUsed++;
          this.openWindows.delete(aura);
          break;
        }
      }
    }
  }

  private sample(world: DeepReadonly<World>): void {
    const u = world.units[this.playerId];
    if (!u || !u.alive || !this.fighting(world, u)) return;
    this.c.samples++;
    if (u.cast || u.gcdUntil > world.time) return;
    let cs;
    try {
      cs = this.queries.controlState(u.id);
    } catch {
      return;
    }
    if (!cs.canAct || !cs.canCast) return;
    const tgt = u.targetId ? world.units[u.targetId] : undefined;
    const enemyOk = !!tgt && tgt.alive && tgt.faction === 'enemy';
    if (u.role !== 'healer' && !enemyOk) return;
    const cls = u.classId ? this.content.class(u.classId) : undefined;
    for (const b of ROTATION) {
      const base = abilityForBind(cls, b);
      if (!base) continue;
      const id = u.abilityOverrides?.[base] ?? base;
      const ab = this.content.ability(id);
      if (!ab) continue;
      try {
        if (this.queries.canCast(u.id, id, targetFor(ab, u.id, enemyOk ? (u.targetId ?? null) : null, tgt?.faction === 'party' ? (u.targetId ?? null) : null), null).ok) {
          this.c.idle++;
          return;
        }
      } catch {
        /* ignore */
      }
    }
  }
}

function targetFor(ab: DeepReadonly<AbilityDef>, self: UnitId, enemy: UnitId | null, ally: UnitId | null): UnitId | null {
  switch (ab.targeting) {
    case 'enemy':
      return enemy;
    case 'ally':
      return ally ?? self;
    case 'any':
      return enemy ?? ally ?? self;
    default:
      return null;
  }
}
